import React, { useState } from 'react';
import { Table2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DrillDownModal, { formatCurrency, formatPercent, formatDate, renderStatusBadge } from './DrillDownModal';

/**
 * KPI Card with Drill-Down
 * Clickable KPI that opens detailed data table
 * Rows in the table navigate to /proposal/{row.id} when clicked
 */
export function KPIDrillDown({ title, value, subtitle, data, columns, icon: Icon, color = 'blue' }) {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-100',
    green: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100',
    amber: 'bg-amber-50 text-amber-600 group-hover:bg-amber-100',
    red: 'bg-red-50 text-red-600 group-hover:bg-red-100',
    purple: 'bg-purple-50 text-purple-600 group-hover:bg-purple-100',
  };

  const borderClasses = {
    blue: 'border-blue-100 hover:border-blue-200',
    green: 'border-emerald-100 hover:border-emerald-200',
    amber: 'border-amber-100 hover:border-amber-200',
    red: 'border-red-100 hover:border-red-200',
    purple: 'border-purple-100 hover:border-purple-200',
  };

  return (
    <>
      <div 
        className={`card-modern p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group relative overflow-hidden ${borderClasses[color]}`}
        onClick={() => data && data.length > 0 && setShowModal(true)}
      >
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        
        <div className="relative flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{title}</p>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
            {subtitle && <p className="text-xs text-slate-600 mt-2 font-medium">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-2xl ${colorClasses[color]} transition-all duration-300 group-hover:scale-110 shadow-sm`}>
            {Icon && <Icon className="w-6 h-6" />}
          </div>
        </div>

        {/* View Details indicator (appears on hover) */}
        {data && data.length > 0 && (
          <div className="absolute bottom-3 right-3 flex items-center gap-1.5 text-primary font-medium text-xs opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
            <Table2 className="w-3.5 h-3.5" />
            <span>View Details</span>
          </div>
        )}
      </div>

      <DrillDownModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={title}
        subtitle={`Detailed breakdown of ${title.toLowerCase()}`}
        data={data || []}
        columns={columns || []}
        onRowClick={(row) => {
          const proposalId = row.id || row.collection_id;
          if (proposalId) {
            setShowModal(false);
            navigate(`/proposal/${proposalId}`);
          }
        }}
      />
    </>
  );
}

/**
 * Chart with Drill-Down Button
 * Adds a drill-down button to any chart component
 */
export function ChartWithDrillDown({ title, subtitle, children, data, columns, className = '' }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className={`bg-white rounded-xl border border-slate-200 p-6 ${className}`}>
      {/* Header with Drill-Down Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
        </div>
        {data && data.length > 0 && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors font-medium text-sm"
          >
            <Table2 className="w-4 h-4" />
            View Details ({data.length})
          </button>
        )}
      </div>

      {/* Chart Content */}
      {children}

      {/* Drill-Down Modal */}
      <DrillDownModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={title}
        subtitle={subtitle}
        data={data || []}
        columns={columns || []}
      />
    </div>
  );
}

/**
 * Common column definitions for reuse
 */
export const commonColumns = {
  proposal: [
    { key: 'collection_name', label: 'Proposal Name', sortable: true },
    { key: 'municipality', label: 'Municipality', sortable: true },
    { key: 'state', label: 'State', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: renderStatusBadge },
    { key: 'created_at', label: 'Created', sortable: true, render: formatDate },
    { key: 'submitted_at', label: 'Submitted', sortable: true, render: formatDate },
    { key: 'total_gross_revenue', label: 'Gross Revenue', sortable: true, render: formatCurrency },
    { key: 'total_net_revenue', label: 'Net Revenue', sortable: true, render: formatCurrency },
  ],
  
  service: [
    { key: 'service_name', label: 'Service', sortable: true },
    { key: 'collection_name', label: 'Proposal', sortable: true },
    { key: 'municipality', label: 'Municipality', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: renderStatusBadge },
    { key: 'created_at', label: 'Date', sortable: true, render: formatDate },
    { key: 'net_revenue', label: 'Net Revenue', sortable: true, render: formatCurrency },
  ],
  
  pipelineHealth: [
    { key: 'collection_name', label: 'Proposal', sortable: true },
    { key: 'municipality', label: 'Municipality', sortable: true },
    { key: 'state', label: 'State', sortable: true },
    { key: 'submitted_at', label: 'Submitted', sortable: true, render: formatDate },
    { key: 'days_pending', label: 'Days Pending', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: renderStatusBadge },
    { key: 'total_net_revenue', label: 'Value', sortable: true, render: formatCurrency },
  ],
  
  revenue: [
    { key: 'period', label: 'Period', sortable: true },
    { key: 'total_gross_revenue', label: 'Gross Revenue', sortable: true, render: formatCurrency },
    { key: 'total_merchant_fees', label: 'Merchant Fees', sortable: true, render: formatCurrency },
    { key: 'total_net_revenue', label: 'Net Revenue', sortable: true, render: formatCurrency },
    { key: 'gpm', label: 'GPM', sortable: true, render: formatPercent },
    { key: 'proposal_count', label: 'Proposals', sortable: true },
  ],
};

export default ChartWithDrillDown;
